import { useFormik } from "formik";
import { useSpeechToText } from "hooks/useSpeechToText";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { SoapnotesInputType } from "../entities/create";
import { EncounterDetails } from "../entities/issues";

type SoapNotesPropType = {
  onCloseSoapnote: () => void;
  onSoapnoteSubmit: (input: Partial<SoapnotesInputType>) => void;
  message: { message: string | null; isError: boolean };
  encounter?: EncounterDetails;
};

type FormInputProps = {
  title: string;
  name: string;
  value: string;
  onChange?: any;
  onMicClick: () => void;
};
const FormInput = React.forwardRef<HTMLTextAreaElement, FormInputProps>(
  (props, ref) => {
    const { value, title, name, onChange, onMicClick } = props;
    return (
      <div className="mb-4 border-[#D9D9D9] bg-[#F7F7F7] border rounded-xl h-[190px]">
        <div className="flex items-center justify-between w-full bg-[#A82784] h-9 rounded-t-xl px-4">
          <p className="text-white font-semibold">{title}</p>
          <button onClick={onMicClick}>
            <img src="/icons/mic-icon.svg" alt="mic" width={16} height={16} />
          </button>
        </div>
        <textarea
          name={name}
          value={value}
          ref={ref}
          onChange={onChange}
          className="p-2 h-[150px] resize-none active:outline-none active:border-none rounded-b-xl bg[#F7F7F7] outline-none text-sm w-full "
        ></textarea>
      </div>
    );
  }
);

FormInput.displayName = "FormInput";

/**
 *
 * This is for entering the medical practitioner's notes and is tied to a particular visit or patient encounter
 *
 * The microphone icon is used to activate and deactivate the speach to text functionality for the soapnote.
 *- N/b This is browser specific and won't work on very old or unsupported browsers
 */

const SoapNotes = (props: SoapNotesPropType) => {
  const { onCloseSoapnote, onSoapnoteSubmit, message, encounter } = props;

  const initialValues = useMemo(() => {
    if (encounter && encounter.soap_notes.length) {
      return encounter.soap_notes[0];
    }
    return null;
  }, [encounter]);

  const subjectiveRef = useRef<HTMLTextAreaElement | null>(null);
  const objectiveRef = useRef<HTMLTextAreaElement | null>(null);
  const assessmentRef = useRef<HTMLTextAreaElement | null>(null);
  const planRef = useRef<HTMLTextAreaElement | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      subjective: initialValues?.subjective ?? "",
      objective: initialValues?.objective ?? "",
      assessment: initialValues?.assessment ?? "",
      plan: initialValues?.plan ?? "",
      reason: encounter?.reason ?? "",
    },
    onSubmit: async (values) => {
      onSoapnoteSubmit(values);
    },
    enableReinitialize: true,
  });

  const [activeField, setActiveField] = useState("");

  const {
    text,
    startListening,
    stopListening,
    isListening,
    error,
    message: speechMessage,
  } = useSpeechToText(values[activeField as keyof typeof values]);

  useEffect(() => {
    if (speechMessage) {
      toast.info(speechMessage);
    } else if (error) {
      toast.error(error);
    }
  }, [speechMessage, error]);

  const handleSpeechToText = (field: string) => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
      setActiveField(field);
    }
  };

  useEffect(() => {
    if (text) {
      setFieldValue(activeField, text);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, setFieldValue]);

  return (
    <div className="h-screen border gap-2  w-[301px] border-[#EFF1F3] shadow-lg rounded-lg  px-4 pt-6 bg-[#EFF1F3]">
      <div>
        <div className="flex justify-between w-full items-center">
          <p className="text-xs font-medium leading-3">NOTE TAKING</p>

          <div className="flex items-center gap-4">
            <button type="button" onClick={onCloseSoapnote}>
              <img
                src="/icons/close-icon-3.svg"
                alt="close"
                width={12}
                height={12}
              />
            </button>
            <button type="button" onClick={() => handleSubmit()}>
              <img
                src="/icons/circle-checked-icon.svg"
                alt="close soapnotes"
                width={17}
                height={17}
              />
            </button>
          </div>
        </div>
        <h4 className="font-extrabold text-lg mt-2">SOAP</h4>
      </div>
      <div className="h-[80%] overflow-scroll mt-6">
        <FormInput
          name="subjective"
          value={values.subjective}
          onChange={handleChange}
          title="SUBJECTIVE"
          onMicClick={() => handleSpeechToText("subjective")}
          ref={subjectiveRef}
        />
        <FormInput
          name="objective"
          value={values.objective}
          onChange={handleChange}
          title="OBJECTIVE"
          onMicClick={() => handleSpeechToText("objective")}
          ref={objectiveRef}
        />
        <FormInput
          name="assessment"
          value={values.assessment}
          onChange={handleChange}
          ref={assessmentRef}
          title="ASSESSMENT & PLAN"
          onMicClick={() => handleSpeechToText("assessment")}
        />
        <FormInput
          name="plan"
          ref={planRef}
          value={values.plan}
          onChange={handleChange}
          title="ORDERS"
          onMicClick={() => handleSpeechToText("plan")}
        />
        <FormInput
          name="reason"
          value={values.reason}
          onChange={handleChange}
          title="REASON"
          ref={reasonRef}
          onMicClick={() => handleSpeechToText("reason")}
        />

        {message.message && (
          <div
            className={`${
              message.isError ? "bg-red-500" : "bg-green-500"
            }text-white py-1 bg-green-500 rounded-lg items-center text-center relative`}
          >
            {message.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SoapNotes;
